import { useTranslate } from "ra-core";
import { ReferenceField } from "@/components/admin/reference-field";
import {
  AvatarFallback,
  AvatarImage,
  Avatar as ShadcnAvatar,
} from "@/components/ui/avatar";

import type { Sale, Task } from "../types";

const OwnerChip = ({
  label,
  initials,
  src,
}: {
  label: string;
  initials: string;
  src?: string;
}) => (
  <span aria-label={label} title={label} className="shrink-0 self-start pt-0.5">
    <ShadcnAvatar className="w-[25px] h-[25px]">
      <AvatarImage src={src} />
      <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
    </ShadcnAvatar>
  </span>
);

/**
 * The account manager who owns a task, as an avatar at the row's edge.
 *
 * Only cross-manager lists show it: spelled out on every row, the name doubles
 * the length of the line carrying the account and the due date, which are what
 * the list is actually scanned by.
 */
export const TaskOwnerAvatar = ({ task }: { task: Task }) => {
  const translate = useTranslate();

  if (task.sales_id == null) {
    // An unowned task is the one most likely to need picking up, so it gets a
    // chip of its own rather than an empty slot that reads as "not applicable".
    return (
      <OwnerChip
        label={translate("resources.tasks.owner.unassigned")}
        initials="?"
      />
    );
  }

  return (
    <ReferenceField<Task, Sale>
      source="sales_id"
      reference="sales"
      record={task}
      link={false}
      render={({ referenceRecord }) =>
        referenceRecord ? (
          <OwnerChip
            label={`${referenceRecord.first_name} ${referenceRecord.last_name}`}
            initials={`${referenceRecord.first_name?.charAt(0) ?? ""}${
              referenceRecord.last_name?.charAt(0) ?? ""
            }`.toUpperCase()}
            src={referenceRecord.avatar?.src ?? undefined}
          />
        ) : null
      }
    />
  );
};
